import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { PostScene, SinglePost } from '~/components/business/SinglePost'
import { GlobalRequest } from '~/components/global-requset'
import Carousel from '~/components/pages/home/Carousel'
import LayoutSwitch from '~/components/ui/LayoutSwitch'
import Loader from '~/components/pages/home/Loader'
import Search from '~/components/pages/home/Search'
import Sort from '~/components/pages/home/Sort'
import Tabs from '~/components/pages/home/Tabs'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { PAGE_SIZE } from '~/configs'
import {
  GET_COMPETITION_LIST,
  GET_TABS,
  SEARCH,
  GET_HOME_BANNER_LIST,
} from '~/graphqls/gqls'
import {
  CopyrightAiTextSearchParam,
  getCompetitionList,
  getCompetitionListVariables,
  getCompetitionList_marketingModule_competitionPage_edges_node,
  getTabs,
  getTabs_aiArtworkResourceModule_aiSquareTab,
  PostTypes,
  search,
  searchVariables,
  getHomeBannerList,
  getHomeBannerListVariables,
  SystemCode,
} from '~/graphqls/types'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import useLayoutState from '~/hooks/useLayoutState'
import { Layout } from '~/layouts/default'
import {
  CacheValueProps,
  getGraphData,
  getScrollCache,
  setScrollCache,
} from '~/utils'
import { getSearchParams, isSearchParamsSame } from '~/utils/search'
import { SEOHead } from '~/components/business/SEOHead'
import useScrollTop from '~/hooks/useScrollTop'
import { useMasonry } from '~/hooks/useMasonry'
import useInfiniteScrollCursor from '~/hooks/useInfiniteScrollCursor'

/** 大赛详情字段 */
interface IAICompetitionContentObj {
  /**
   * pc-banner图
   */
  banner_pc: string | null
  /**
   * wujieai用的背景图
   */
  banner_cover: string | null
  /**
   * pc-活动介绍
   */
  desc_pc: string | null
  /**
   * 主题
   *  */
  theme: string
}

/** 单个大赛详情 */
interface IAICompetitionDetailNode
  extends getCompetitionList_marketingModule_competitionPage_edges_node,
    IAICompetitionContentObj {}

/** 字段格式化 */
function formatListRes(
  res?: getCompetitionList_marketingModule_competitionPage_edges_node | null
) {
  const contentObj: IAICompetitionContentObj =
    res?.content && JSON.parse(res?.content)
  return {
    node: {
      ...res,
      ...contentObj,
    },
  }
}

interface Props {
  bannerList: IAICompetitionDetailNode[]
  tabs: getTabs_aiArtworkResourceModule_aiSquareTab
  resultList: PostTypes[]
  hasNextPage: boolean
  total: number
  scrollCache: CacheValueProps
  endCursor: string
}

const Home = ({
  bannerList,
  tabs,
  resultList,
  hasNextPage: _hasNextPage,
  total: _total,
  scrollCache,
  endCursor: _endCursor,
}: Props) => {
  const scrollTop = useScrollTop()
  const router = useRouter()
  const [colCount, setColCount] = useLayoutState('homepage-layout', '6')
  const [loading, setLoading] = useState(false)

  const [extraQueryParams, setExtraQueryParams] = useState<
    Partial<CopyrightAiTextSearchParam>
  >(getSearchParams(router.query, tabs))

  const [initList, setInitList] = useState(resultList)
  console.log(_hasNextPage, '_hasNextPage')
  const [hasNextPage, setHasNextPage] = useState(_hasNextPage)
  const [total, setTotal] = useState(_total)
  const [endCursor, setEndCursor] = useState(_endCursor)

  const passedParams = {
    param: JSON.stringify(extraQueryParams),
    indexurl: encodeURIComponent(router.asPath),
  }

  const { InfiniteScollComp, list } = useInfiniteScrollCursor<
    PostTypes,
    search,
    searchVariables
  >({
    queryDoc: SEARCH,
    initData: initList,
    initPageSize: PAGE_SIZE,
    hasNextPage: hasNextPage,
    endCursor: endCursor,
    extraQueryParams: {
      param: {
        ...(extraQueryParams as CopyrightAiTextSearchParam),
      },
    },
    hasNextPageExtractor: res => {
      return (
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.hasNextPage || false
      )
    },
    endCursorExtractor: res => {
      return (
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo?.endCursor ||
        ''
      )
    },
    dataExtractor: res => {
      return getGraphData<PostTypes>(
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
      )
    },
  })

  const { columnList, columnWidth, columnGutter } = useMasonry<PostTypes>({
    data: list,
    sizeLoader: (it: PostTypes) => {
      if (!it?.coverImage?.height || !it?.coverImage?.width) return 1
      return it?.coverImage?.height / it?.coverImage?.width
    },
    column: Number(colCount),
    gutter: 0,
  })

  const searchAction = useImperativeQuery<search, searchVariables>(SEARCH)
  useDidUpdateEffect(() => {
    setLoading(true)
    searchAction({
      first: PAGE_SIZE,
      param: {
        ...(extraQueryParams as CopyrightAiTextSearchParam),
      },
    })
      .then(res => {
        setInitList(
          getGraphData(
            res.data.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
          )
        )
        setHasNextPage(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.hasNextPage || false
        )
        setTotal(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.totalCount || 0
        )
        setEndCursor(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.endCursor || ''
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [extraQueryParams])

  useDidUpdateEffect(() => {
    const params = getSearchParams(router.query, tabs)
    if (isSearchParamsSame(params, extraQueryParams as any)) {
      return
    }
    setExtraQueryParams(params)
  }, [router.asPath])

  useEffect(() => {
    window.scrollTo({ top: scrollCache.scrollTop || 0, behavior: 'auto' })
  }, [])

  useDidUpdateEffect(() => {
    setScrollCache({
      key: router.asPath,
      value: {
        path: router.asPath,
        scrollTop,
        pageSize: list.length,
      },
    })
  }, [list.length, scrollTop, router.asPath])

  return (
    <Layout headerType="no-margin">
      <SEOHead />
      <section className="hero">
        <div className="w-full h-[380px] relative flex items-center justify-center">
          <div className="absolute top-0 right-0 bottom-0 left-0">
            <Carousel
              list={bannerList.map((item: any) => {
                const [title, subTitle] = item.name!.split('：')
                return {
                  img: (item.banner_cover || item.banner_pc)!,
                  title,
                  subTitle,
                  id: item.id,
                  redirectUrl: item.redirectUrl,
                  isBanner: item.isBanner,
                }
              })}
            />
          </div>
          <div className="w-[694px] relative z-10 p-5">
            <Search />
          </div>
        </div>
      </section>
      <MyContainer>
        <Tabs tabs={tabs} />
        <div className="flex items-center justify-between pt-[18px] pb-4">
          <Sort model={extraQueryParams.model} />
          <div className="flex items-center text-xs">
            {/* 有数据，且keyword为空 */}
            {total !== 0 && extraQueryParams.keyword && (
              <div className="mr-3">
                共找到<span className="text-primary">{total}</span>条结果
              </div>
            )}
            <LayoutSwitch col={colCount} setCol={setColCount} />
          </div>
        </div>
        <section>
          <div className="mb-8">
            <Loader
              cols={Number(colCount)}
              rows={4}
              containerWidth={1392}
              loading={loading}
            >
              {!loading && (
                <>
                  {list.length ? (
                    <>
                      <div className="flex justify-between items-start">
                        {columnList?.map((item, index) => (
                          <div
                            style={{
                              width: columnWidth,
                            }}
                            key={index}
                          >
                            {item?.map((it, i) => (
                              <div
                                key={it.id}
                                style={{
                                  marginBottom: columnGutter,
                                }}
                              >
                                <SinglePost
                                  post={it}
                                  scene={PostScene.RESPONSIVE_SQUARE}
                                  // param={passedParams}
                                  // offset={i * Number(colCount) + index}
                                />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      {InfiniteScollComp}
                    </>
                  ) : (
                    <MyEmpty />
                  )}
                </>
              )}
            </Loader>
          </div>
        </section>
      </MyContainer>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)

  const scrollCache = getScrollCache(context)

  const tabRes = await client.query<getTabs>({
    query: GET_TABS,
    fetchPolicy: 'no-cache',
  })

  if (!tabRes.data.aiArtworkResourceModule?.aiSquareTab) {
    throw new Error('failed to fetch tab data')
  }

  const params = getSearchParams(
    context.query,
    tabRes.data.aiArtworkResourceModule?.aiSquareTab
  )

  console.log(params, 'params')

  const [listRes, resultRes, bannerRes] = await Promise.all([
    client.query<getCompetitionList, getCompetitionListVariables>({
      query: GET_COMPETITION_LIST,
      variables: {
        offset: 0,
        first: 5,
      },
      fetchPolicy: 'no-cache',
    }),
    client.query<search, searchVariables>({
      query: SEARCH,
      variables: {
        first: scrollCache.pageSize || PAGE_SIZE,
        param: params,
      },
      fetchPolicy: 'no-cache',
    }),
    client.query<getHomeBannerList, getHomeBannerListVariables>({
      query: GET_HOME_BANNER_LIST,
      variables: {
        scene: 1,
      },
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          system_code: SystemCode.WUJIE_AI,
        },
      },
    }),
  ])
  // banner
  const bannerList =
    bannerRes.data.marketingModule?.bannerList?.map((it: any) => ({
      ...it,
      banner_cover: it.imageUrl,
      name: it.title,
      isBanner: true,
      startDateTime: it.startDateTime || -Infinity,
    })) || []
  // 大赛
  const competitionList = getGraphData(
    listRes.data.marketingModule?.competitionPage?.edges?.map(it => ({
      ...formatListRes(it?.node),
    })) || []
  )
  let _bannerList = bannerList.slice(0, 3)
  _bannerList = _bannerList.concat(
    competitionList.splice(0, 5 - _bannerList.length)
  )

  return {
    props: {
      initialState,
      scrollCache,
      bannerList: _bannerList.sort(
        (a: any, b: any) => b.startDateTime - a.startDateTime
      ),
      tabs: tabRes.data.aiArtworkResourceModule?.aiSquareTab,
      resultList: getGraphData(
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
      ),
      hasNextPage:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.hasNextPage || false,
      total:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.totalCount || 0,
      endCursor:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.endCursor,
    },
  }
}

export default Home
